import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Phone, MessageCircle, FileText, Download, ChevronRight, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import SafeImage from "@/components/SafeImage";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { FinalCTA } from "@/pages/Home";
import { api, telLink, waLink } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export default function ProductDetail() {
  const { id } = useParams();
  const s = useSettings();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api.get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        return api.get("/products", { params: { category: r.data.category } });
      })
      .then((r) => setRelated(r.data.filter((p) => p.id !== id).slice(0, 4)))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <section className="bg-[#F6F7F8] py-24 text-center" data-testid="product-not-found">
        <h1 className="font-heading text-3xl font-bold">Product not found</h1>
        <Link to="/products" className="mt-6 inline-block bg-[#111111] px-6 py-3 font-semibold text-white" data-testid="not-found-back">
          Back to catalogue
        </Link>
      </section>
    );
  }

  if (!product) {
    return (
      <section className="bg-[#F6F7F8] py-20">
        <div className="mx-auto grid max-w-7xl animate-pulse gap-10 px-4 sm:px-6 lg:grid-cols-2" data-testid="product-loading">
          <div className="aspect-[4/3] bg-[#e5e8ec]" />
          <div className="space-y-4">
            <div className="h-8 w-2/3 bg-[#e5e8ec]" />
            <div className="h-4 w-1/3 bg-[#e5e8ec]" />
            <div className="h-32 w-full bg-[#e5e8ec]" />
          </div>
        </div>
      </section>
    );
  }

  const wa = waLink(s, `Hello Hari Om Trading Co., I want to enquire about: ${product.name} (Model: ${product.model})`);

  return (
    <>
      <SEO
        title={`${product.name}${product.brand ? ` | ${product.brand}` : ""} | Hari Om Trading Co.`}
        description={`Enquire about ${product.name}${product.brand ? ` by ${product.brand}` : ""} at Hari Om Trading Co., Tahliwal, Una. Model, specifications, availability and quotation on request.`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            brand: product.brand && !product.brand.startsWith("[") ? { "@type": "Brand", name: product.brand } : undefined,
            model: product.model && !product.model.startsWith("[") ? product.model : undefined,
            category: product.ptype || product.category,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://hariomtrading.co.in/" },
              { "@type": "ListItem", position: 2, name: "Products", item: "https://hariomtrading.co.in/products" },
              { "@type": "ListItem", position: 3, name: product.category.replace(/-/g, " "), item: `https://hariomtrading.co.in/products?category=${product.category}` },
              { "@type": "ListItem", position: 4, name: product.name },
            ],
          },
        ]}
      />
      <section className="bg-[#F6F7F8] py-10 lg:py-16" data-testid="product-detail">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs font-medium text-[#667085]" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#111111]">Home</Link>
            <ChevronRight size={13} aria-hidden="true" />
            <Link to="/products" className="hover:text-[#111111]">Products</Link>
            <ChevronRight size={13} aria-hidden="true" />
            <Link to={`/products?category=${product.category}`} className="capitalize hover:text-[#111111]">
              {product.category.replace(/-/g, " ")}
            </Link>
            <ChevronRight size={13} aria-hidden="true" />
            <span className="text-[#161616]" aria-current="page">{product.name}</span>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-2">
            <Reveal>
              <div className="clip-frame relative bg-[#202124]">
                <SafeImage src={product.image} alt={product.name} className="aspect-[4/3] w-full object-cover" data-testid="product-image" />
                {product.brand ? (
                  <span className="absolute left-4 top-4 bg-[#F5B700] px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#111111]">
                    {product.brand}
                  </span>
                ) : null}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8a6800]">{product.ptype}</p>
              <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[#161616] sm:text-4xl lg:text-5xl" data-testid="product-name">
                {product.name}
              </h1>
              <p className="mt-3 text-sm font-medium text-[#667085]" data-testid="product-model">
                Model: <span className="text-[#161616]">{product.model || "[ADD MODEL NUMBER]"}</span>
              </p>

              <p className="mt-5 text-base leading-relaxed text-[#667085]">{product.short_desc}</p>

              <p className="mt-6 font-heading text-2xl font-bold text-[#161616]" data-testid="product-price">
                {product.mrp != null ? `MRP ₹${Number(product.mrp).toLocaleString("en-IN")}` : "Contact for Latest Price"}
              </p>
              <p className="mt-2 flex items-center gap-2 text-sm font-medium text-[#667085]" data-testid="product-availability">
                <CheckCircle2 size={15} className="text-[#18864B]" aria-hidden="true" /> {product.availability || "[CONFIRM AVAILABILITY]"}
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                <Link
                  to={`/quote?product=${encodeURIComponent(product.name)}&model=${encodeURIComponent(product.model || "")}`}
                  data-testid="detail-request-quote"
                  className="flex min-h-[52px] items-center justify-center gap-2 bg-[#F5B700] px-4 py-3 font-heading text-[15px] font-semibold text-[#111111] hover:bg-[#D99C00]"
                >
                  <FileText size={16} aria-hidden="true" /> Request Quote
                </Link>
                <a
                  href={wa}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-whatsapp"
                  className="flex min-h-[52px] items-center justify-center gap-2 bg-[#18864B] px-4 py-3 font-heading text-[15px] font-semibold text-white hover:bg-[#126b3c]"
                >
                  <MessageCircle size={16} aria-hidden="true" /> WhatsApp
                </a>
                <a
                  href={telLink(s)}
                  data-testid="detail-call"
                  className="flex min-h-[52px] items-center justify-center gap-2 border-2 border-[#111111] px-4 py-3 font-heading text-[15px] font-semibold text-[#111111] hover:bg-[#111111] hover:text-white"
                >
                  <Phone size={16} aria-hidden="true" /> Call
                </a>
              </div>

              {product.datasheet ? (
                <a href={product.datasheet} target="_blank" rel="noopener noreferrer" data-testid="detail-datasheet" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#111111] underline underline-offset-4 hover:text-[#8a6800]">
                  <Download size={15} aria-hidden="true" /> Download Datasheet
                </a>
              ) : (
                <p className="mt-5 flex items-center gap-2 text-sm text-[#667085]" data-testid="detail-datasheet-placeholder">
                  <Download size={15} aria-hidden="true" /> Datasheet: [ADD DATASHEET LINK]
                </p>
              )}
            </Reveal>
          </div>

          <div className="mt-16 grid gap-5 lg:grid-cols-2">
            <Reveal>
              <div className="h-full border border-[#D9DDE3] bg-white p-8" data-testid="product-specs">
                <h2 className="font-heading text-xl font-semibold text-[#161616]">Technical Specifications</h2>
                <ul className="mt-5 space-y-3">
                  {(product.specs?.length ? product.specs : ["[ADD PRODUCT SPECIFICATION]"]).map((sp, i) => (
                    <li key={i} className="flex gap-3 border-b border-[#eef0f3] pb-3 text-sm text-[#667085] last:border-0">
                      <span className="mt-1.5 h-2 w-2 shrink-0 bg-[#F5B700]" aria-hidden="true" />
                      <span className={sp.startsWith("[") ? "font-mono text-[#98a2b3]" : ""}>{sp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="h-full border border-[#D9DDE3] bg-white p-8" data-testid="product-applications">
                <h2 className="font-heading text-xl font-semibold text-[#161616]">Applications</h2>
                <ul className="mt-5 space-y-3">
                  {(product.applications?.length ? product.applications : ["[ADD APPLICATION]"]).map((ap, i) => (
                    <li key={i} className="flex gap-3 border-b border-[#eef0f3] pb-3 text-sm text-[#667085] last:border-0">
                      <span className="mt-1.5 h-2 w-2 shrink-0 bg-[#F5B700]" aria-hidden="true" />
                      <span className={ap.startsWith("[") ? "font-mono text-[#98a2b3]" : ""}>{ap}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {related.length > 0 && (
            <div className="mt-20" data-testid="related-products">
              <Reveal>
                <h2 className="font-heading text-3xl font-bold tracking-tight text-[#161616]">Related Products</h2>
              </Reveal>
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            </div>
          )}
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
